import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { store } from './store';
import ProtectedRoute from './components/ProtectedRoute';
import UnprotectedRoute from './components/UnprotectedRoute';
import Login from './pages/Login';
import ConversationPage from './pages/ConversationPage';
import SSOPage from './pages/SSOPage';
import TrainingSessionRouter from './pages/TrainingSessionRouter';
import { useSubscriptionMonitor } from './hooks/useSubscriptionMonitor';
import SubscriptionStatusAlert from './components/SubscriptionStatusAlert';
import TermsGates from './components/TermsGates';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import { ToastProvider } from './contexts/ToastContext';

// Mirrors the --signature / --signature-strong / --ink tokens in
// src/styles/tokens.css (Foundry copper, graphite ink headings). MUI's
// palette and style overrides need literal hex/px for their internal color
// math, so this is a literal mirror, not a live CSS-var reference. This is
// what keeps MUI-heavy pages (e.g. TrainingSessionPage) visually consistent
// with the Tailwind-styled pages without editing every Typography/Button
// call site individually.
const theme = createTheme({
  palette: {
    primary: {
      main: '#A6531E',
      dark: '#8A4417',
    },
    secondary: {
      main: '#423F39',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      '"Fira Sans"',
      '"Droid Sans"',
      '"Helvetica Neue"',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 600, color: '#1C1B18' },
    h2: { fontWeight: 600, color: '#1C1B18' },
    h3: { fontWeight: 600, color: '#1C1B18' },
    h4: { fontWeight: 600, color: '#1C1B18' },
    h5: { fontWeight: 500, color: '#1C1B18' },
    h6: { fontWeight: 500, color: '#1C1B18' },
  },
  shape: {
    borderRadius: 8, // matches --radius-control; card-level overrides below
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16, // matches --radius-card
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16, // matches --radius-card
          border: '1px solid #E6E1D7', // matches --hairline
        },
      },
    },
  },
});

// Inner component to use hooks
const AppContent: React.FC = () => {
  // Monitor subscription status and auto-logout if expired
  useSubscriptionMonitor();

  return (
    <>
      {/* Subscription status warning alert */}
      <SubscriptionStatusAlert />

      {/* Blocking post-login gates: EUTC acceptance, forced password change */}
      <TermsGates />

      {/* New-build prompt: pinned on every route, including login */}
      <UpdateAvailableBanner />

      <div className="min-h-screen bg-paper">
        <Routes>
          <Route path="/" element={<Navigate to="/conversation" replace />} />
          <Route
            path="/login"
            element={
              <UnprotectedRoute>
                <Login />
              </UnprotectedRoute>
            }
          />
          <Route path="/sso" element={<SSOPage />} />
          <Route
            path="/conversation"
            element={
              <ProtectedRoute>
                <ConversationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training/session/:sessionId"
            element={
              <ProtectedRoute>
                <TrainingSessionRouter />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/conversation" replace />} />
        </Routes>
      </div>
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
